import { useEffect } from 'react'

import { APP_HOST, isProduction, MARKETING_HOST } from '../config/config'
import { withCrossDomainIds } from './crossDomainTracking'

// Portal ID matches each landing page's HubSpotFormEmbed (owns Ads conversion sync).
const HUBSPOT_TRACKING_PORTAL_ID = '246335544'
const HUBSPOT_TRACKING_REGION = 'na2'

type HostKind = 'marketing' | 'app'

export interface HostGuardResult {
  /** True while a redirect is in flight — render nothing (or `navigateTo`). */
  redirecting: boolean
  /**
   * Same-origin path to hand to react-router's `<Navigate>`. Non-null only for
   * the marketing-route-on-app-host case; cross-host hops are handled inside the
   * hook via `location.replace` and leave this null.
   */
  navigateTo: string | null
}

/**
 * In production/staging the marketing pages and the product app live on separate
 * hosts (`www.cai.ai` / `app.cai.insure`, `www.cai-staging.insure` /
 * `app.cai-staging.insure`). Wrong-host loads resolve in a single hop:
 *
 * - Private route on the marketing host → `location.replace` to the app host,
 *   preserving path + query + hash (deep links keep working). A new document is
 *   unavoidable here: it's a different origin.
 * - Marketing route on the app host → `navigateTo: '/insure'`, which the caller
 *   renders as `<Navigate replace />`. This is the *same* origin, so routing it
 *   through react-router keeps the loaded bundle and the react-query cache
 *   alive — `location.replace` would tear the document down and re-run auth +
 *   `/api/me` from scratch.
 *
 * No-op when the host pair is undefined (dev/preview — single origin) and during
 * SSR/prerender (no `window`). `redirecting` is true while either hop is in
 * flight, so the caller can render nothing and avoid flashing content that's
 * about to be navigated away.
 */
export function useHostGuard(kind: HostKind): HostGuardResult {
  const hostsKnown = Boolean(APP_HOST && MARKETING_HOST) && typeof window !== 'undefined'

  let externalTarget: string | null = null
  let navigateTo: string | null = null
  if (kind === 'app' && hostsKnown && window.location.hostname === MARKETING_HOST) {
    const url = new URL(window.location.href)
    url.host = APP_HOST
    // Carry the PostHog person/session across the origin hop, same as a CTA click.
    externalTarget = withCrossDomainIds(url.toString())
  } else if (kind === 'marketing' && hostsKnown && window.location.hostname === APP_HOST.split(':')[0]) {
    navigateTo = '/insure'
  }

  useEffect(() => {
    if (externalTarget) window.location.replace(externalTarget)
  }, [externalTarget])

  // Loads HubSpot's tracking script here so every marketing route gets it for free.
  useEffect(() => {
    if (kind !== 'marketing' || navigateTo || !isProduction || typeof window === 'undefined') return
    const src = `https://js-${HUBSPOT_TRACKING_REGION}.hs-scripts.com/${HUBSPOT_TRACKING_PORTAL_ID}.js`
    if (document.querySelector(`script[src="${src}"]`)) return
    const script = document.createElement('script')
    script.id = 'hs-script-loader'
    script.src = src
    script.async = true
    script.defer = true
    document.body.appendChild(script)
  }, [kind, navigateTo])

  return { redirecting: externalTarget !== null || navigateTo !== null, navigateTo }
}

/**
 * True when a prerendered document is being served on the app host, in which case
 * its HTML must be thrown away rather than hydrated.
 *
 * Only marketing routes prerender (MarketingLayout's children and /fuelapp), and
 * both bounce to `/insure` via `useHostGuard('marketing')`. Prerendering runs with no
 * `window`, so that guard is a no-op at build time and the emitted HTML carries
 * the whole marketing tree — including PageLoader's `fixed inset-0 z-10000`
 * splash. Hydrating that on the app host has the client render `<Navigate>` where
 * the server wrote a full page; the mismatch strands the splash above the app as
 * an opaque overlay that only goes away if you delete the node by hand. Client
 * rendering clears the container first, so nothing survives.
 *
 * Reloading a private route never hit this: those aren't prerendered, so the
 * container is empty and entry-client already client-rendered.
 */
export function shouldDiscardPrerenderedDocument(container: Element, hostname: string): boolean {
  return Boolean(APP_HOST) && hostname === APP_HOST && container.firstChild !== null
}
